import React, { useEffect, useState } from "react";
import CenterSpin from "../../misc/spin";
import "./percepciones.css";
import { getPerceptionSurveyData } from "../../../services/dashboard";
import { COUNTRY_OPTIONS } from "../../../utils/constants/countryOptions";
import CardContainer from "../CardContainer";
import CardPercepciones from "../CardPercepciones";
import {
  getComparableCountryOptions,
  getCorrectCategoryNames,
  getCorrectVariableNames,
  getQuestionOptions,
  getVariableOptions,
  getWaveOptions,
} from "./functions";
import TEXT_MISC from "../../../utils/text/misc.json";

const VARIABLES_TO_IGNORE = [
  "V114_politics",
  "Q240_politics",
  "V95_politics",
  "l1_politics",
];

const QUESTIONS_COLUMNS = [
  //'p38n_Q2',
  'p4st_Q1'
]

export default function Percepciones(props) {
  const [country, setCountry] = useState(props.country);
  const [countryProps, setCountryProps] = useState(props.countryProps);
  const [isDataAvailable, setIsDataAvailable] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInitialData, setIsLoadingInitialData] = useState();
  const [isDataLoading, setIsDataLoading] = useState(true);

  const [graphSettings, setGraphSettings] = useState({
    waveOptions: [],
    questionOptions: [],
    variableOptions: [],
    comparableCountryOptions: [],
    selectedWave: null,
    selectedQuestion: null,
    selectedVariable: null,
    selectedComparableCountries: [],
    currentGraphType: "single-country-general",
  });

  const [graphData, setGraphData] = useState({
    type: null,
    data: null,
  });

  function getQuestionLabel() {
    let questionData = graphSettings.questionOptions.find(
      (e) => e.code === graphSettings.selectedQuestion
    );
    if (questionData === undefined) return "";

    return questionData.label;
  }

  function getDownloadCardSubtitle() {
    let subtitle;

    subtitle =
      props.language === "en"
        ? countryProps["country_text_en"]
        : countryProps["country_text"];
    subtitle += ", ";
    subtitle +=
      props.title +
      " (" +
      graphSettings.waveOptions.find(
        (option) => option.code === graphSettings.selectedWave
      ).label +
      ")";

    return subtitle;
  }

  async function retrieveData(settings) {
    if (
      !(
        settings.selectedWave &&
        settings.selectedQuestion &&
        settings.selectedVariable
      )
    )
      return;

    try {
      const { data } = await getPerceptionSurveyData(
        country,
        settings.selectedWave,
        settings.selectedQuestion,
        settings.selectedVariable
      );

      if (data === "no_data") {
        setIsDataAvailable(false);
        return;
      }
      
      data.categories = getCorrectCategoryNames(
        props,
        settings.selectedWave,
        settings.selectedQuestion,
        data.categories.slice()
      );
      data.country = country;

      if (settings.selectedVariable !== "GENERAL") {
        data.variables = getCorrectVariableNames(
          props,
          settings.selectedWave,
          settings.selectedVariable,
          data.variables.slice()
        );

        if (
          props.optionsTree[settings.selectedWave].variables[
            settings.selectedVariable
          ].show_only
        ) {
          data.show_only =
            props.optionsTree[settings.selectedWave].variables[
              settings.selectedVariable
            ].show_only;
        }
      }

      const readyData = [data];

      if (settings.selectedComparableCountries.length > 0) {
        for (let i = 0; i < settings.selectedComparableCountries.length; i++) {
          let response = await getPerceptionSurveyData(
            settings.selectedComparableCountries[i],
            settings.selectedWave,
            settings.selectedQuestion,
            settings.selectedVariable
          );

          readyData.push({
            country: settings.selectedComparableCountries[i],
            categories: response.data.categories.map((cat) => {
              return props.optionsTree[settings.selectedWave].questions[
                settings.selectedQuestion
              ].values[cat];
            }),
            data: response.data.data,
          });
        }
      }
      setGraphData({
        type: graphSettings.currentGraphType,
        data: readyData,
      });
      setIsDataAvailable(true);
    } catch (err) {
      setIsDataAvailable(false);
      console.log(
        `Error retrieving data for: ${country}, ${settings.selectedWave}, ${settings.selectedQuestion}, ${settings.selectedVariable}`
      );
      console.log(err);
    }
  }

  useEffect(() => {
    async function updateData() {
      setIsDataLoading(true);
      await retrieveData(graphSettings);
      setIsDataLoading(false);
    }

    updateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphSettings]);

  function handleSelectClear() {
    handleComparableCountriesChange([]);
  }

  async function handleWaveChange(newWaveCode) {
    const newQuestionOptions = getQuestionOptions(props, country, newWaveCode);
    const newVariableOptions = getVariableOptions(
      props,
      VARIABLES_TO_IGNORE,
      newWaveCode
    );

    const newQuestionCode = newQuestionOptions[0].code;
    const newVariableCode = newVariableOptions[0].code;
    const returnedComparableCountryOptions = getComparableCountryOptions(
      props,
      country,
      newWaveCode,
      newQuestionCode
    );

    setGraphSettings({
      ...graphSettings,
      selectedWave: newWaveCode,
      selectedQuestion: newQuestionCode,
      selectedVariable: newVariableCode,
      questionOptions: newQuestionOptions,
      variableOptions: newVariableOptions,
      comparableCountryOptions: returnedComparableCountryOptions,
      selectedComparableCountries: [],
      currentGraphType: QUESTIONS_COLUMNS.includes(newQuestionCode) ? "single-country-general-column" : "single-country-general",
    });
  }

  async function handleQuestionChange(newQuestionCode) {
    if (
      props.type === "lapop" ||
      graphSettings.selectedComparableCountries.length === 0
    ) {
      let selectedCompCountries, newCompCountriesOptions;
      if (props.type === "lapop") {
        newCompCountriesOptions = getComparableCountryOptions(
          props,
          country,
          graphSettings.selectedWave,
          newQuestionCode
        );
        selectedCompCountries = [];
      }

      let currentGraphType = "single-country-general";
      if(graphSettings.selectedVariable !== "GENERAL") {
        currentGraphType = QUESTIONS_COLUMNS.includes(newQuestionCode) ? "single-country-other-column" : "single-country-other";
      } else  {
        currentGraphType = QUESTIONS_COLUMNS.includes(newQuestionCode) ? "single-country-general-column" : "single-country-general";
      }

      console.log("currentGraphType", currentGraphType);

      setGraphSettings({
        ...graphSettings,
        selectedQuestion: newQuestionCode,
        selectedComparableCountries:
          selectedCompCountries ?? graphSettings.selectedComparableCountries,
        comparableCountryOptions:
          newCompCountriesOptions ?? graphSettings.selectedComparableCountries,
        currentGraphType: currentGraphType
      });
    } else {
      let currentGraphType = "single-country-general";
      if(graphSettings.selectedVariable !== "GENERAL") {
        currentGraphType = QUESTIONS_COLUMNS.includes(newQuestionCode) ? "single-country-other-column" : "single-country-other";
      } else {
        currentGraphType = QUESTIONS_COLUMNS.includes(newQuestionCode) ? "single-country-general-column" : "single-country-general";
      }
      console.log("currentGraphType", currentGraphType);
      setGraphSettings({
        ...graphSettings,
        selectedQuestion: newQuestionCode,
        currentGraphType: currentGraphType
      });
    }
  }

  async function handleVariableChange(newVariableCode) {
    if (newVariableCode !== "GENERAL") {
      console.log("newVariableCode", newVariableCode);
      console.log("graphSettings.selectedQuestion", graphSettings.selectedQuestion);
      console.log("QUESTIONS_COLUMNS.includes(graphSettings.selectedQuestion)", QUESTIONS_COLUMNS.includes(graphSettings.selectedQuestion));
      setGraphSettings({
        ...graphSettings,
        selectedVariable: newVariableCode,
        selectedComparableCountries: [],
        currentGraphType: QUESTIONS_COLUMNS.includes(graphSettings.selectedQuestion) ? "single-country-other-column" : "single-country-other",
      });
    } else {
      setGraphSettings({
        ...graphSettings,
        selectedVariable: newVariableCode,
        selectedComparableCountries: [],
        currentGraphType: QUESTIONS_COLUMNS.includes(graphSettings.selectedQuestion) ? "single-country-general-column" : "single-country-general",
      });
    }
  }

  async function handleComparableCountriesChange(newValue) {
    if (newValue.length > 3) {
      // TODO, alert that maximum is 3
      return;
    } else if (newValue.length === 0) {
      setGraphSettings({
        ...graphSettings,
        selectedComparableCountries: newValue,
        currentGraphType: QUESTIONS_COLUMNS.includes(graphSettings.selectedQuestion) ? "single-country-general-column" : "single-country-general",
      });

      return;
    }

    setGraphSettings({
      ...graphSettings,
      selectedComparableCountries: newValue,
      currentGraphType: QUESTIONS_COLUMNS.includes(graphSettings.selectedQuestion) ? "single-country-general-column" : "single-country-general",
    });
  }

  useEffect(() => {
    async function getInitialDataAndOptions() {
      setIsLoading(true);
      setIsLoadingInitialData(true);

      const returnedWaveOptions = getWaveOptions(props, country);
      for (let i = 0; i < returnedWaveOptions.length; ) {
        let currentWaveQuestions = getQuestionOptions(
          props,
          country,
          returnedWaveOptions[i].code
        );

        if (currentWaveQuestions.length === 0) {
          returnedWaveOptions.splice(i, 1);
        } else {
          i++
        }
      }

      let firstWaveOptionCode;

      if (returnedWaveOptions.length === 0) {
        setIsDataAvailable(false);
        setIsDataLoading(false);
        setIsLoading(false);

        setGraphSettings({
          ...graphSettings,
          waveOptions: [],
          questionOptions: [],
          variableOptions: [],
          comparableCountryOptions: [],
          selectedWave: "",
          selectedQuestion: "",
          selectedVariable: "",
          selectedComparableCountries: [],
          currentGraphType: "",
        });

        return;
      } else {
        firstWaveOptionCode = returnedWaveOptions[0].code;
      }

      const returnedQuestionOptions = getQuestionOptions(
        props,
        country,
        firstWaveOptionCode
      );

      const returnedVariableOptions = getVariableOptions(
        props,
        VARIABLES_TO_IGNORE,
        firstWaveOptionCode
      );

      const firstQuestionOptionCode = returnedQuestionOptions[0].code;
      const firstVariableOptionCode = returnedVariableOptions[0].code;
      const returnedComparableCountryOptions = getComparableCountryOptions(
        props,
        country,
        firstWaveOptionCode,
        firstQuestionOptionCode
      );

      await retrieveData({
        ...graphSettings,
        waveOptions: returnedWaveOptions,
        questionOptions: returnedQuestionOptions,
        variableOptions: returnedVariableOptions,
        comparableCountryOptions: returnedComparableCountryOptions,
        selectedWave: firstWaveOptionCode,
        selectedQuestion: firstQuestionOptionCode,
        selectedVariable: firstVariableOptionCode,
      });

      let currentGraphType = "single-country-general"
      if(QUESTIONS_COLUMNS.includes(firstQuestionOptionCode)) {
        currentGraphType = "single-country-general-column"
      }
      setGraphSettings({
        ...graphSettings,
        waveOptions: returnedWaveOptions,
        questionOptions: returnedQuestionOptions,
        variableOptions: returnedVariableOptions,
        comparableCountryOptions: returnedComparableCountryOptions,
        selectedWave: firstWaveOptionCode,
        selectedQuestion: firstQuestionOptionCode,
        selectedVariable: firstVariableOptionCode,
        currentGraphType: currentGraphType
      });

      setIsLoading(false);
      setIsDataLoading(false);
      setIsLoadingInitialData(false);
    }

    getInitialDataAndOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country]);

  useEffect(() => {
    setCountry(props.country);
    setCountryProps(COUNTRY_OPTIONS.find((opt) => opt.code === props.country));
    handleSelectClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.country, props.countryProps]);

  useEffect(() => {
    if (graphSettings.selectedWave && graphSettings.selectedQuestion) {
      const questions = getQuestionOptions(
        props,
        country,
        graphSettings.selectedWave
      );
      const variables = getVariableOptions(
        props,
        VARIABLES_TO_IGNORE,
        graphSettings.selectedWave
      );
      const comparableCountries = getComparableCountryOptions(
        props,
        country,
        graphSettings.selectedWave,
        graphSettings.selectedQuestion
      );

      setGraphSettings({
        ...graphSettings,
        variableOptions: variables,
        questionOptions: questions,
        comparableCountryOptions: comparableCountries,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.optionsTree]);

  return isLoading ? (
    <div style={{ height: "100%" }}>
      <CenterSpin />
    </div>
  ) : (
    <CardContainer>
      {isLoadingInitialData ? (
        <CenterSpin />
      ) : (
        <div id={props.type}>
          <CardPercepciones
            countryProps={countryProps}
            isLoading={isLoading}
            isDataLoading={isDataLoading}
            isDataAvailable={isDataAvailable}
            cardTitle={props.title}
            cardSource={
              (props.language === "en" ? "Source: " : "Fuente: ") +
              (props.title +
                " (" +
                graphSettings.waveOptions.find(
                  (option) => option.code === graphSettings.selectedWave
                ).label +
                ")")
            }
            methodology={props.methodology}
            description={props.description}
            currentGraphType={graphData.type}
            tableData={graphData.data}
            selectArray={[
              {
                selectedValue: graphSettings.selectedWave,
                changeHandler: handleWaveChange,
                options: graphSettings.waveOptions,
                placeHolderText: TEXT_MISC["selectYear"][props.language],
                labelAttributeName: "label",
                valueAttributeName: "code",
                ignoreTranslation: true,
                isDisabled: isDataLoading,
              },
              {
                selectedValue: graphSettings.selectedVariable,
                changeHandler: handleVariableChange,
                options: graphSettings.variableOptions,
                placeHolderText: TEXT_MISC["selectVariable"][props.language],
                labelAttributeName: "label",
                valueAttributeName: "code",
                ignoreTranslation: true,
                isDisabled: isDataLoading,
              },
            ]}
            questionSelect={{
              selectedValue: graphSettings.selectedQuestion,
              changeHandler: handleQuestionChange,
              options: graphSettings.questionOptions,
              placeHolderText: TEXT_MISC["selectQuestion"][props.language],
              labelAttributeName: "label",
              valueAttributeName: "code",
              ignoreTranslation: true,
              isDisabled: isDataLoading,
            }}
            multipleSelectArray={[
              {
                options: graphSettings.comparableCountryOptions,
                selectedOptions: graphSettings.selectedComparableCountries,
                changeHandler: handleComparableCountriesChange,
                handleClear: handleSelectClear,
                isDisabled:
                  graphSettings.selectedVariable !== "GENERAL" ||
                  graphSettings.waveOptions.length === 0 ||
                  isDataLoading,
                placeHolderText: TEXT_MISC["compareCountries"][props.language],
                defaultValue: [],
                labelAttributeName: "label",
                valueAttributeName: "code",
                language: props.language,
              },
            ]}
            language={props.language}
            downloadableCardID={props.downloadableCardID}
            downloadableCardFileName={props.downloadableCardFileName}
            downloadableCardTitle={getQuestionLabel()}
            downloadableCardSubtitle={getDownloadCardSubtitle()}
          />
        </div>
      )}
    </CardContainer>
  );
}
